import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SeV2QuestionDto,
  useGetSourcingEventQuery,
  usePublishInformationMutation,
} from "../../../../autogen/bff-api";
import { AnonymizationBanner } from "../../../../common/components/communication/AnonymizationBanner";
import { AttachmentPicker } from "../../../../common/components/communication/AttachmentPicker";
import { richTextHasContent } from "../../../../common/components/communication/communicationUtils";
import { useMessageAttachments } from "../../../../common/components/communication/useMessageAttachments";
import { useApiError } from "../../../../common/errors/useApiError";
import { TipTap } from "../../../../common/input/TipTap/TipTap";

interface Props {
  eventId: string;
  fromQuestion?: SeV2QuestionDto | null;
  initialAnswer?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const PublishInformationModal = ({ eventId, fromQuestion, initialAnswer, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const isQa = !!fromQuestion;
  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const [publish, { isLoading }] = usePublishInformationMutation();
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const requestDocuments = (event?.eventData.documents ?? []).map((d) => ({ id: d.id, name: d.name }));
  const attachments = useMessageAttachments({
    eventId,
    uploadEntityType: "SeV2AnnouncementAttachment",
    pollRole: "buyer",
  });

  useEffect(() => {
    if (isOpen) {
      // Q&A: prefill the question (to anonymize) and the answer separately. Standalone: a single message.
      // Prefer the unsaved inline answer draft (captured at open time) over the saved answer, so the
      // buyer doesn't have to retype what they already wrote in the inline answer field.
      setTitle(fromQuestion ? fromQuestion.subject : "");
      setQuestion(fromQuestion?.content ?? "");
      const draft = initialAnswer && richTextHasContent(initialAnswer) ? initialAnswer : null;
      setAnswer(draft ?? fromQuestion?.answer?.content ?? "");
      attachments.reset();
      setResetKey((key) => key + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fromQuestion]);

  const canSubmit =
    title.trim().length > 0 && richTextHasContent(answer) && (!isQa || richTextHasContent(question));

  const handlePublish = async () => {
    if (!canSubmit) return;
    try {
      await publish({
        eventId,
        createInformationRequest: {
          title: title.trim(),
          content: answer,
          fromQuestionId: fromQuestion?.id ?? null,
          question: isQa ? question : null,
          attachments: attachments.toInputs(),
        },
      }).unwrap();
      onClose();
    } catch (error) {
      displayer.trigger(error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Information to tenderers")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <AnonymizationBanner />
            <FormControl isRequired>
              <FormLabel>{t("Title")}</FormLabel>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("Title") ?? ""} />
            </FormControl>
            {isQa && (
              <FormControl isRequired>
                <FormLabel>{t("Question")}</FormLabel>
                <TipTap
                  key={`q-${resetKey}`}
                  content={question}
                  editable
                  debounceWait={0}
                  onUpdate={(value) => setQuestion(value)}
                />
              </FormControl>
            )}
            <FormControl isRequired>
              <FormLabel>{isQa ? t("Answer") : t("Message")}</FormLabel>
              <TipTap
                key={`a-${resetKey}`}
                content={answer}
                editable
                debounceWait={0}
                onUpdate={(value) => setAnswer(value)}
              />
            </FormControl>
            <AttachmentPicker
              controls={attachments}
              requestDocuments={requestDocuments}
            />
          </Stack>
        </ModalBody>
        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            colorScheme="teal"
            onClick={handlePublish}
            isLoading={isLoading}
            isDisabled={!canSubmit || attachments.isUploading || attachments.hasFailedUploads}
          >
            {t("Send to all tenderers")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
